<template>
  <g class="world-editor-room">
    <!-- Selection ring, drawn outside the plate so it never covers content -->
    <rect
        v-if="selected"
        :height="roomHeight + 6"
        :width="roomWidth + 6"
        :x="roomLeft - 3"
        :y="roomTop - 3"
        class="room-selection-ring"
        fill="none"
        rx="10"
        ry="10"
    />

    <!-- Room plate -->
    <rect
        :height="roomHeight"
        :stroke="roomStroke"
        :stroke-width="selected ? 1.75 : 1"
        :width="roomWidth"
        :x="roomLeft"
        :y="roomTop"
        class="room-rect"
        fill="var(--gm-surface-2)"
        rx="7"
        ry="7"
    />

    <!-- Plate interior clip: the icon and its scrim stay inside the rounded plate -->
    <clipPath :id="clipId">
      <rect
          :height="roomHeight"
          :width="roomWidth"
          :x="roomLeft"
          :y="roomTop"
          rx="7"
          ry="7"
      />
    </clipPath>

    <g :clip-path="`url(#${clipId})`">
      <!-- Room icon fills the plate; held back so badges and the name stay first -->
      <image
          v-if="backgroundImage"
          :height="roomHeight"
          :href="backgroundImageSrc"
          :width="roomWidth"
          :x="roomLeft"
          :y="roomTop"
          class="room-icon"
          preserveAspectRatio="xMidYMid slice"
      />

      <!-- Type accent: a bar down the leading edge, not a fill that hides the room -->
      <rect
          v-if="showTypeIndicator"
          :fill="typeIndicatorColor"
          :height="roomHeight"
          :x="roomLeft"
          :y="roomTop"
          class="room-type-bar"
          width="3"
      />

      <!-- Name scrim -->
      <rect
          v-if="roomName"
          :height="nameBandHeight"
          :width="roomWidth"
          :x="roomLeft"
          :y="roomTop + roomHeight - nameBandHeight"
          :fill="`url(#${scrimId})`"
      />
    </g>

    <linearGradient :id="scrimId" x1="0" x2="0" y1="0" y2="1">
      <stop offset="0%" stop-color="#0c0e0f" stop-opacity="0"/>
      <stop offset="100%" stop-color="#0c0e0f" stop-opacity="0.88"/>
    </linearGradient>

    <!-- Room name -->
    <text
        v-if="roomName"
        :x="roomLeft + roomWidth / 2"
        :y="roomTop + roomHeight - 4"
        class="room-name"
        text-anchor="middle"
    >{{ roomName }}
    </text>

    <!-- Occupancy strip: every layer present, in one fixed order along the top edge -->
    <g v-if="badges.length" class="entity-indicators">
      <g
          v-for="(badge, i) in badges"
          :key="badge.key"
          :class="['entity-group', `${badge.key}-indicator`]"
          @click="showEntityDetails(badge.key)"
      >
        <title>{{ badge.tooltip }}</title>
        <rect
            :fill="badge.color"
            :x="badgeX(i)"
            :y="roomTop - badgeRadius"
            :width="badgeSize"
            :height="badgeSize"
            :rx="badge.shape === 'round' ? badgeRadius : 3"
            :ry="badge.shape === 'round' ? badgeRadius : 3"
            class="entity-badge"
            stroke="#0c0e0f"
            stroke-width="1.25"
        />
        <text
            :x="badgeX(i) + badgeRadius"
            :y="roomTop + 3"
            class="entity-count"
            text-anchor="middle"
        >{{ badge.count }}
        </text>
      </g>
    </g>

    <!-- Stairs sit on the trailing edge: clear of the occupancy strip above and
         the room name below, so a room can carry all three at once. -->
    <g v-if="hasVerticalConnections" class="stairs-indicators">
      <title>{{ stairsTooltip }}</title>
      <rect
          :x="roomLeft + roomWidth - 6"
          :y="roomTop + roomHeight / 2 - stairsPlateHeight / 2"
          :height="stairsPlateHeight"
          class="stairs-plate"
          fill="var(--gm-surface-0)"
          rx="6"
          ry="6"
          stroke="var(--gm-border)"
          stroke-width="1"
          width="12"
      />
      <polygon
          v-if="verticalConnectionDirections.up"
          :points="stairsArrowUp"
          class="stairs-arrow"
      />
      <polygon
          v-if="verticalConnectionDirections.down"
          :points="stairsArrowDown"
          class="stairs-arrow"
      />
    </g>
  </g>
</template>

<script>
import {WorldEditorConfig, WorldEditorLayer} from '@/models/WorldEditorModels.ts';
import {RoomLabel} from "@/utils/mapData.js";

export default {
  name: 'WorldEditorRoom',
  props: {
    room: {
      type: Object,
      required: true
    },
    cellSize: {
      type: Number,
      default: 80
    },
    cellPadding: {
      type: Number,
      default: 35
    },
    selected: {
      type: Boolean,
      default: false
    },
    editorState: {
      type: Object,
      default: () => ({})
    },
    activeLayers: {
      type: Set,
      default: () => new Set()
    },
    showName: {
      type: Boolean,
      default: true
    },
    // Precomputed by the map: {up, down}, or null when the room has no stairs.
    verticalLink: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      roomType: null,
      roomLabel: null,
      backgroundImage: null,
    };
  },
  created() {
    this.extractLabels();
    this.loadIcon();
  },
  methods: {
    extractLabels() {
      const labels = this.room.labels || [];
      this.roomType = labels[0] || null;
      this.roomLabel = labels[1] || null;
    },
    loadIcon() {
      const icon = RoomLabel.getIcon(this.roomLabel);
      if (icon instanceof HTMLImageElement) {
        this.backgroundImage = icon;
      } else {
        this.backgroundImage = null;
      }
    },
    badgeX(index) {
      const stripStart = this.roomLeft + (this.roomWidth - this.badgeStripWidth) / 2;
      return stripStart + index * (this.badgeSize + this.badgeGap);
    },
    showEntityDetails(entityType) {
      // Emit an event to show detailed information about the entities
      this.$emit('show-entity-details', {
        roomId: this.room.id,
        entityType: entityType,
        entities: this.room[entityType]
      });
    },
    getPlayerTooltip() {
      if (!this.room.players.length) return 'No players';

      let tooltip = `Players (${this.room.players.length}):\n`;
      const playerNames = this.room.players
          .map(player => player.name || 'Unnamed Player')
          .slice(0, 5);

      tooltip += playerNames.join('\n');

      if (this.room.players.length > 5) {
        tooltip += `\n...and ${this.room.players.length - 5} more`;
      }

      return tooltip;
    },
    getNpcTooltip() {
      if (!this.room.npcs.length) return 'No NPCs';

      let tooltip = `NPCs (${this.room.npcs.length}):\n`;
      const npcNames = this.room.npcs
          .map(npc => npc.name || 'Unnamed NPC')
          .slice(0, 5);

      tooltip += npcNames.join('\n');

      if (this.room.npcs.length > 5) {
        tooltip += `\n...and ${this.room.npcs.length - 5} more`;
      }

      return tooltip;
    },
    getObjectTooltip() {
      if (!this.room.objects.length) return 'No objects';

      let tooltip = `Objects (${this.room.objects.length}):\n`;
      const objectNames = this.room.objects
          .map(obj => obj.name || 'Unnamed Object')
          .slice(0, 5);

      tooltip += objectNames.join('\n');

      if (this.room.objects.length > 5) {
        tooltip += `\n...and ${this.room.objects.length - 5} more`;
      }

      return tooltip;
    },
    getAnomalyTooltip() {
      if (!this.room.anomalies.length) return 'No anomalies';

      let tooltip = `Anomalies (${this.room.anomalies.length}):\n`;
      const anomalyNames = this.room.anomalies
          .map(anomaly => anomaly.name || 'Unnamed Anomaly')
          .slice(0, 5);

      tooltip += anomalyNames.join('\n');

      if (this.room.anomalies.length > 5) {
        tooltip += `\n...and ${this.room.anomalies.length - 5} more`;
      }

      return tooltip;
    },
    getSpawnerTooltip() {
      if (!this.room.spawners.length) return 'No spawners';

      let tooltip = `Spawners (${this.room.spawners.length}):\n`;
      const spawnerNames = this.room.spawners
          .map(spawner => spawner.spawner_type || 'Unknown Spawner')
          .slice(0, 5);

      tooltip += spawnerNames.join('\n');

      if (this.room.spawners.length > 5) {
        tooltip += `\n...and ${this.room.spawners.length - 5} more`;
      }

      return tooltip;
    }
  },
  computed: {
    // Background image source
    backgroundImageSrc() {
      return this.backgroundImage ? this.backgroundImage.src : null;
    },
    // Unique ID for the clipPath to avoid conflicts
    clipId() {
      return `clip-${this.room.id}`;
    },
    // Room positioning
    roomLeft() {
      return this.room.position.grid_x * this.cellSize + this.cellPadding / 2;
    },
    roomTop() {
      return this.room.position.grid_y * this.cellSize + this.cellPadding / 2;
    },
    roomWidth() {
      return this.cellSize - this.cellPadding;
    },
    roomHeight() {
      return this.cellSize - this.cellPadding;
    },

    // Room styling. A selected room keeps its dark plate — the ring and the
    // brighter edge carry the selection, so the room's contents stay readable.
    roomStroke() {
      return this.selected ? 'var(--gm-accent-hi)' : 'var(--gm-border)';
    },

    scrimId() {
      return `scrim-${this.room.id}`;
    },

    roomName() {
      if (!this.showName) return '';
      const name = typeof this.room.getDisplayName === 'function'
          ? this.room.getDisplayName()
          : this.room.name;
      if (!name) return '';
      return name.length > 16 ? `${name.slice(0, 15)}…` : name;
    },

    nameBandHeight() {
      return Math.round(this.roomHeight * 0.42);
    },

    // Occupancy strip geometry
    badgeSize() {
      return 13;
    },
    badgeRadius() {
      return this.badgeSize / 2;
    },
    badgeGap() {
      return 2;
    },
    badgeStripWidth() {
      return this.badges.length * this.badgeSize + (this.badges.length - 1) * this.badgeGap;
    },

    // Fixed order, so the strip is read the same way in every room.
    badges() {
      const spec = [
        {key: 'players', shape: 'round', tooltip: () => this.getPlayerTooltip()},
        {key: 'anomalies', shape: 'round', tooltip: () => this.getAnomalyTooltip()},
        {key: 'npcs', shape: 'square', tooltip: () => this.getNpcTooltip()},
        {key: 'objects', shape: 'square', tooltip: () => this.getObjectTooltip()},
        {key: 'spawners', shape: 'round', tooltip: () => this.getSpawnerTooltip()}
      ];
      const visible = {
        players: this.showPlayers,
        anomalies: this.showAnomalies,
        npcs: this.showNpcs,
        objects: this.showObjects,
        spawners: this.showSpawners
      };

      return spec
          .filter(entry => visible[entry.key] && (this.room[entry.key]?.length || 0) > 0)
          .map(entry => ({
            key: entry.key,
            shape: entry.shape,
            color: this.layerColors[entry.key],
            count: this.room[entry.key].length,
            tooltip: entry.tooltip()
          }));
    },

    stairsTooltip() {
      const {up, down} = this.verticalConnectionDirections;
      if (up && down) return 'Stairs up and down';
      return up ? 'Stairs up' : 'Stairs down';
    },

    stairsPlateHeight() {
      const {up, down} = this.verticalConnectionDirections;
      return up && down ? 22 : 13;
    },

    stairsArrowUp() {
      const cx = this.roomLeft + this.roomWidth;
      const cy = this.roomTop + this.roomHeight / 2 - (this.verticalConnectionDirections.down ? 5 : 0);
      return `${cx - 3},${cy + 1.8} ${cx + 3},${cy + 1.8} ${cx},${cy - 2.4}`;
    },

    stairsArrowDown() {
      const cx = this.roomLeft + this.roomWidth;
      const cy = this.roomTop + this.roomHeight / 2 + (this.verticalConnectionDirections.up ? 5 : 0);
      return `${cx - 3},${cy - 1.8} ${cx + 3},${cy - 1.8} ${cx},${cy + 2.4}`;
    },

    // Type indicator
    showTypeIndicator() {
      return this.room.type && this.room.type !== 'default';
    },
    typeIndicatorColor() {
      switch (this.room.type) {
        case 'special':
          return '#bb9bf0';
        case 'secret':
          return '#f2c85c';
        case 'dangerous':
          return '#f0776a';
        default:
          return 'var(--gm-border)';
      }
    },

    // Layer visibility
    showPlayers() {
      return this.activeLayers.has(WorldEditorLayer.PLAYERS);
    },
    showNpcs() {
      return this.activeLayers.has(WorldEditorLayer.NPCS);
    },
    showObjects() {
      return this.activeLayers.has(WorldEditorLayer.OBJECTS);
    },
    showAnomalies() {
      return this.activeLayers.has(WorldEditorLayer.ANOMALIES);
    },
    showSpawners() {
      return this.activeLayers.has(WorldEditorLayer.SPAWNERS);
    },

    // Entity presence
    hasEntities() {
      return this.room.players.length > 0 ||
          this.room.npcs.length > 0 ||
          this.room.objects.length > 0 ||
          this.room.anomalies.length > 0 ||
          this.room.spawners.length > 0;
    },

    // Layer colors
    layerColors() {
      return {
        players: WorldEditorConfig.layerColors[WorldEditorLayer.PLAYERS],
        npcs: WorldEditorConfig.layerColors[WorldEditorLayer.NPCS],
        objects: WorldEditorConfig.layerColors[WorldEditorLayer.OBJECTS],
        anomalies: WorldEditorConfig.layerColors[WorldEditorLayer.ANOMALIES],
        spawners: WorldEditorConfig.layerColors[WorldEditorLayer.SPAWNERS]
      };
    },

    // Vertical connections (stairs)
    hasVerticalConnections() {
      return this.verticalConnectionDirections.up || this.verticalConnectionDirections.down;
    },

    verticalConnectionDirections() {
      if (this.verticalLink) {
        return this.verticalLink;
      }

      return this.derivedVerticalDirections;
    },

    // Fallback for any caller that does not precompute the links.
    derivedVerticalDirections() {
      if (!this.editorState || !this.editorState.rooms || !this.editorState.connections) {
        return {up: false, down: false};
      }

      const directions = {up: false, down: false};
      const currentRoomZ = this.room.position.grid_z;

      Array.from(this.editorState.connections.values()).forEach(connection => {
        if (!connection.isVertical) return;

        if (connection.fromRoomId === this.room.id) {
          const toRoom = this.editorState.rooms.get(connection.toRoomId);
          if (toRoom && toRoom.position.grid_z > currentRoomZ) {
            directions.up = true;
          } else if (toRoom && toRoom.position.grid_z < currentRoomZ) {
            directions.down = true;
          }
        } else if (connection.toRoomId === this.room.id) {
          const fromRoom = this.editorState.rooms.get(connection.fromRoomId);
          if (fromRoom && fromRoom.position.grid_z > currentRoomZ) {
            directions.up = true;
          } else if (fromRoom && fromRoom.position.grid_z < currentRoomZ) {
            directions.down = true;
          }
        }
      });

      return directions;
    }
  },
  watch: {
    room: {
      immediate: true,
      handler() {
        this.extractLabels();
        this.loadIcon();
      },
    },
    // Labels are replaced in place on the same room object, so watching `room`
    // alone never fires and the map keeps drawing the previous marker.
    'room.labels': {
      deep: true,
      handler() {
        this.extractLabels();
        this.loadIcon();
      },
    },
  }
};
</script>

<style scoped>
.world-editor-room {
  transition: filter var(--gm-duration) var(--gm-ease);
}

.room-rect {
  transition: stroke var(--gm-duration) var(--gm-ease);
}

.world-editor-room:hover .room-rect {
  stroke: var(--gm-border);
}

.room-selection-ring {
  stroke: var(--gm-accent-hi);
  stroke-width: 1;
  opacity: 0.55;
  pointer-events: none;
}

.room-icon {
  opacity: 0.42;
  pointer-events: none;
}

.world-editor-room:hover .room-icon {
  opacity: 0.6;
}

.room-type-bar {
  pointer-events: none;
}

.room-name {
  fill: var(--gm-text);
  font-family: var(--gm-font-body);
  font-size: 7.5px;
  letter-spacing: 0.02em;
  pointer-events: none;
  user-select: none;
}

/* Occupancy strip */
.entity-badge {
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.65));
}

.entity-count {
  fill: #0c0e0f;
  font-family: var(--gm-font-display);
  font-size: 8px;
  font-weight: 700;
  pointer-events: none;
  user-select: none;
}

.entity-group {
  cursor: pointer;
}

.entity-group:hover .entity-badge {
  stroke: var(--gm-text);
  stroke-width: 1.5;
}

/* Stairs */
.stairs-indicators {
  pointer-events: none;
}

.stairs-plate {
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.65));
}

.stairs-arrow {
  fill: var(--gm-accent);
}
</style>